import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ITEM_TAB_NAME, PREFERENCE_TAB, PAGE_TAB_NAME_LIST, PRODUCT_TAB, PROFILE_TAB, RECIPE_TAB, USERS_TAB, GENERATION_TRACE_TAB, REFERENCE_BOOKS_TAB, GRAIN_LIQUID_RATIOS_TAB, SVG_LIBRARY_TAB} from '../constant';
import { MenuLogoIcon } from '../appearence/icons/small_logo_icon';
import { useTranslation } from 'react-i18next';
import { ChevronIcon } from '../appearence/icons/chevron_icon';
import {
    Carrot,
    CookingPot,
    Heart,
    Library,
    ListTree,
    LogOut,
    Ratio,
    Shapes,
    UserRound,
    Users,
} from 'lucide-react';
import Modal from './Modal';
import { MODAL_SMALL_SIZE } from './helper';
import CommitInfo from '../shared/CommitInfo';
import { useAuth } from '../auth/AuthContext';
import { Button, cn } from '@chapche/ui';

const ExitModalContent = ({handleClose, handleLogout}) => {
    const { i18n } = useTranslation();
    return (
        <>
            <h2 className="modal__title modal__logout-title block__title-h3">{i18n.t('public.app.page.login.modal.title')}</h2>
            <p className="block__content modal__text modal__logout-text">{i18n.t('public.app.page.login.modal.content')}</p>
            <div className="modal__button-group modal__logout-buttons">
                <Button
                    variant="outline"
                    className="modal__logout-button_outline"
                    onClick={() => handleClose(false)}
                >
                    {i18n.t('public.app.page.login.modal.cancel')}
                </Button>
                <Button className="modal__logout-button" onClick={handleLogout}>
                    {i18n.t('public.app.page.login.modal.exit')}
                </Button>
            </div>
        </>
    );
};

const MENU_ICON_STROKE = 1.75;

const MAIN_MENU_LIST = [
    {
        title: PROFILE_TAB,
        link: PROFILE_TAB,
        icon: UserRound,
    },
    {
        title: USERS_TAB,
        link: USERS_TAB,
        icon: Users,
    },
    {
        title: PRODUCT_TAB,
        link: `${PRODUCT_TAB}/${ITEM_TAB_NAME}/${PAGE_TAB_NAME_LIST}`,
        icon: Carrot,
    },
    {
        title: PREFERENCE_TAB,
        link: `${PREFERENCE_TAB}/${ITEM_TAB_NAME}/${PAGE_TAB_NAME_LIST}`,
        icon: Heart,
    },
    {
        title: RECIPE_TAB,
        link: `${RECIPE_TAB}/${ITEM_TAB_NAME}/${PAGE_TAB_NAME_LIST}`,
        icon: CookingPot,
    },
    {
        title: GENERATION_TRACE_TAB,
        link: GENERATION_TRACE_TAB,
        icon: ListTree,
    },
    {
        title: REFERENCE_BOOKS_TAB,
        link: REFERENCE_BOOKS_TAB,
        icon: Library,
    },
    {
        title: GRAIN_LIQUID_RATIOS_TAB,
        link: GRAIN_LIQUID_RATIOS_TAB,
        icon: Ratio,
    },
    {
        title: SVG_LIBRARY_TAB,
        link: SVG_LIBRARY_TAB,
        icon: Shapes,
    },

];

const MainPage = ({
    menuIsOpen,
    setMenuIsOpen,
    menuCollapsed = false,
    setMenuCollapsed,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [exitModalIsOpen, setExitModalIsOpen] = useState(false);
    const { i18n } = useTranslation();
    const { logout } = useAuth();

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    return (
        <>
            {menuIsOpen ? (
                <div
                    className="menu__wrapper fixed inset-0 z-[120] bg-background opacity-90"
                    onClick={() => setMenuIsOpen(false)}
                />
            ) : null}
            {exitModalIsOpen ?
                <Modal content={<ExitModalContent handleClose={setExitModalIsOpen} handleLogout={handleLogout} />}
                    handleCloseModal={setExitModalIsOpen} size={MODAL_SMALL_SIZE}/>
                :
                null}
            <nav
                className={cn(
                    'menu sticky top-0 z-[99] flex min-h-screen shrink-0 flex-col items-center self-start transition-[width,flex-basis] duration-200 ease-in-out',
                    menuCollapsed ? 'menu_collapsed !w-[72px] !basis-[72px]' : 'w-sidebar basis-sidebar',
                    'max-[1024px]:fixed max-[1024px]:top-0 max-[1024px]:bottom-0 max-[1024px]:z-[120] max-[1024px]:w-[min(280px,80vw)] max-[1024px]:basis-auto',
                )}
            >
                <div className="menu__content sticky top-0 flex h-screen w-full flex-col items-center">
                    <div
                        className={cn(
                            'menu__logo-group relative mx-auto w-fit',
                            menuCollapsed ? 'mt-24 mb-28' : 'mt-[44px] mb-60',
                        )}
                    >
                        <MenuLogoIcon
                            nameOfClass={cn(
                                'menu__logo_small',
                                menuCollapsed ? 'h-40 w-40 min-h-40' : 'h-80 w-80 min-h-80',
                            )}
                        />
                        {!menuCollapsed ? <CommitInfo /> : null}
                    </div>
                    <ul
                        className={cn(
                            'menu__list m-0 list-none overflow-y-auto p-0',
                            menuCollapsed ? 'w-full' : 'w-3/4',
                        )}
                        key={i18n.resolvedLanguage}
                    >
                        {MAIN_MENU_LIST.map(it => (
                            <li
                                key={it.title}
                                className={cn('menu__item navigation-link block', menuCollapsed ? 'mb-4' : 'mb-10')}
                            >
                                <Link
                                    className="menu__item-link flex flex-col items-center"
                                    to={'/' + it.link}
                                    title={i18n.t(`app.page.${it.title}.title`)}
                                >
                                    <it.icon
                                        className={cn(
                                            'menu__icon inline-block',
                                            location.pathname.includes(it.title) && 'menu__icon_active',
                                        )}
                                        strokeWidth={MENU_ICON_STROKE}
                                        aria-hidden
                                    />
                                    <h2
                                        className={cn(
                                            'block__content menu__title mt-12 mb-[30px] inline-block',
                                            menuCollapsed && 'sr-only',
                                            location.pathname.includes(it.title) && 'menu__title_active',
                                        )}
                                    >
                                        {i18n.t(`app.page.${it.title}.title`)}
                                    </h2>
                                </Link>
                            </li>
                        ))}
                    </ul>
                    <div
                        className={cn(
                            'menu__footer mt-auto flex shrink-0 flex-col items-center',
                            menuCollapsed ? 'mb-16 w-full' : 'mb-24 w-3/4',
                        )}
                    >
                        {typeof setMenuCollapsed === 'function' ? (
                            <button
                                type="button"
                                className="menu__collapse-toggle appearance-none shrink-0 cursor-pointer border-0 bg-transparent px-4 pt-4 pb-8 leading-none"
                                onClick={() => setMenuCollapsed(!menuCollapsed)}
                                title={i18n.t(
                                    menuCollapsed
                                        ? 'app.page.menu.expand'
                                        : 'app.page.menu.collapse',
                                )}
                                aria-label={i18n.t(
                                    menuCollapsed
                                        ? 'app.page.menu.expand'
                                        : 'app.page.menu.collapse',
                                )}
                                aria-expanded={!menuCollapsed}
                            >
                                <ChevronIcon
                                    nameOfClass={cn(
                                        'menu__collapse-icon h-10 w-[18px]',
                                        menuCollapsed
                                            ? 'menu__collapse-icon_collapsed -rotate-90'
                                            : 'rotate-90',
                                    )}
                                />
                            </button>
                        ) : null}
                        <ul className="menu__list menu__list_bottom m-0 h-auto min-h-0 w-full list-none overflow-hidden p-0">
                            <li
                                className="menu__item menu__item-logout navigation-link mt-auto block pt-32"
                                onClick={() => setExitModalIsOpen(true)}
                            >
                                <Link
                                    className="menu__item-link flex flex-col items-center"
                                    to={'#'}
                                    title={i18n.t('public.app.page.login.exit')}
                                >
                                    <LogOut
                                        className="menu__icon inline-block"
                                        strokeWidth={MENU_ICON_STROKE}
                                        aria-hidden
                                    />
                                    <h2 className={cn('block__button menu__title mt-12 mb-[30px] inline-block', menuCollapsed && 'sr-only')}>
                                        {i18n.t('public.app.page.login.exit')}
                                    </h2>
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>
        </>
    );
};

export default MainPage;
